import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Receipt, ArrowLeft, FileText, Send, CheckCircle2, Copy, Mail, AlertTriangle, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import Modal from '../components/ui/Modal';
import { usd, StatusChip, ErrorBox, Field, Labeled, openAuthed, inputCls, btnCls, btn2Cls } from '../lib/q2c';
import { myEmail, amAdmin } from '../lib/taskAccess';
import { canRemovePayment } from '../lib/invoicePayments.mjs';
import { easternDay } from '../../modules/q2c/eastern-day.mjs';

const FILTERS = [['open', 'Open'], ['overdue', 'Overdue'], ['draft', 'Drafts'], ['paid', 'Paid'], ['all', 'All']];
const BUCKETS = [['current', 'Not yet due'], ['d1_30', '1–30 days'], ['d31_60', '31–60'], ['d61_90', '61–90'], ['d90_plus', '90+']];

export default function InvoicesPage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('open');
  const [aging, setAging] = useState(null);
  const [messages, setMessages] = useState([]);
  // Show the empty state only after the current list request succeeds.
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const seq = useRef(0);
  const statusRef = useRef(status);
  statusRef.current = status;
  const load = useCallback(async () => {
    if (status !== statusRef.current) return; // an action's reload for a filter we've left does nothing
    const mine = ++seq.current; // only the latest request may update the page
    try {
      const [l, a, m] = await Promise.all([api.get(`/invoices?status=${status}`), api.get('/invoices/aging'), api.get('/invoices/messages?status=pending')]);
      if (mine !== seq.current) return;
      setRows(l.invoices || []); setAging(a); setMessages(m.messages || []); setError(''); setLoaded(true);
    } catch (e) { if (mine === seq.current) { setError(e.message); setLoaded(false); } }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  // Hide stale invoices after the current filter fails to load.
  const shown = loaded ? rows : [];

  async function draftReminders() {
    setError(''); setNotice('');
    try { const r = await api.post('/invoices/reminders/draft', {}); setNotice(`${r.created.length} reminder draft${r.created.length === 1 ? '' : 's'} created.`); load(); } catch (e) { setError(e.message); }
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><Receipt size={20} /> Invoices & collections</h1>
          <p className="text-sm text-[var(--text-secondary)]">Milestone invoices are drafted automatically (order placed, shipment departed, delivered). Nothing is sent without a partner's approval.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={btn2Cls} onClick={draftReminders}><Mail size={13} className="mr-1 inline" />Draft due reminders</button>
          <button className={btn2Cls} onClick={() => setSettingsOpen(true)}>Settings</button>
        </div>
      </div>
      <ErrorBox msg={error} />
      {notice && <p className="mb-3 rounded bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

      {aging && (
        <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-3"><h2 className="text-sm font-semibold">Accounts receivable aging</h2><span className="text-sm">Total open <b>{usd(aging.total)}</b>{aging.late_fees_accrued > 0 ? ` · late charges accrued ${usd(aging.late_fees_accrued)}` : ''}</span></div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {BUCKETS.map(([k, l]) => <Field key={k} label={l}><span className={k !== 'current' && aging.buckets[k] > 0 ? 'font-semibold text-red-600' : ''}>{usd(aging.buckets[k])}</span></Field>)}
          </div>
          {aging.customers.length > 0 && (
            <div className="mt-3 overflow-x-auto"><table className="w-full min-w-[560px] text-xs"><thead><tr className="text-left text-[var(--text-secondary)]"><th className="py-1">Customer</th>{BUCKETS.map(([k, l]) => <th key={k} className="text-right">{l}</th>)}<th className="text-right">Total</th></tr></thead><tbody>
              {aging.customers.map((c) => <tr key={c.customer} className="border-t border-[var(--border)]"><td className="py-1">{c.customer} ({c.invoices})</td>{BUCKETS.map(([k]) => <td key={k} className="text-right">{c[k] ? usd(c[k]) : '—'}</td>)}<td className="text-right font-semibold">{usd(c.total)}</td></tr>)}
            </tbody></table></div>
          )}
        </section>
      )}

      {messages.length > 0 && (
        <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
          <h2 className="mb-2 text-sm font-semibold">Reminders waiting for a partner ({messages.length})</h2>
          <div className="space-y-2">{messages.map((m) => <ReminderCard key={m.id} m={m} onChanged={load} />)}</div>
        </section>
      )}

      <div className="mb-2 flex overflow-hidden rounded-lg border border-[var(--border)] w-fit">
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1.5 text-xs font-medium ${status === k ? 'bg-primary text-white' : 'bg-white text-[var(--text-secondary)] dark:bg-slate-800'}`}>{l}</button>)}
      </div>
      {/* Phones: one card per invoice; the table is for wider screens. */}
      <div className="space-y-2 sm:hidden">
        {shown.map((i) => (
          <Link key={i.id} to={`/invoices/${i.id}`} className="block rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="break-words font-medium text-[var(--text-primary)]">{i.customer_name || 'No customer'}</p>
                <p className="break-words text-xs text-[var(--text-secondary)]"><span className="font-mono">{i.number}</span>{i.auto_created ? ' · auto' : ''} · {i.title}</p>
              </div>
              <div className="shrink-0 text-right"><p className="font-semibold">{usd(i.balance)}</p><StatusChip status={i.display_status} /></div>
            </div>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">Due {i.due_date || `${i.terms_days}d after issue`}{i.days_overdue > 0 ? <span className="ml-1 text-red-600">({i.days_overdue}d late)</span> : null} · total {usd(i.total)}</p>
          </Link>
        ))}
        {loaded && !rows.length && <p className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-secondary)]">No invoices here.</p>}
      </div>
      <div className="hidden overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900 sm:block">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[var(--text-secondary)]">
            <th className="px-3 py-2">No.</th><th className="px-3 py-2">Customer / title</th><th className="px-3 py-2">Due</th>
            <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2">Status</th>
          </tr></thead>
          <tbody>
            {shown.map((i) => (
              <tr key={i.id} className="border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800">
                <td className="px-3 py-2 font-mono text-xs"><Link className="text-primary" to={`/invoices/${i.id}`}>{i.number}</Link>{i.auto_created && <span className="ml-1 text-[10px] text-[var(--text-secondary)]">auto</span>}</td>
                <td className="px-3 py-2"><div>{i.customer_name || '—'}</div><div className="text-xs text-[var(--text-secondary)]">{i.title}</div></td>
                <td className="px-3 py-2 text-xs">{i.due_date || `${i.terms_days}d after issue`}{i.days_overdue > 0 ? <span className="ml-1 text-red-600">({i.days_overdue}d late)</span> : null}</td>
                <td className="px-3 py-2 text-right">{usd(i.total)}</td>
                <td className="px-3 py-2 text-right font-medium">{usd(i.balance)}</td>
                <td className="px-3 py-2"><StatusChip status={i.display_status} /></td>
              </tr>
            ))}
            {loaded && !rows.length && <tr><td colSpan={6} className="p-6 text-center text-[var(--text-secondary)]">No invoices here.</td></tr>}
          </tbody>
        </table>
      </div>
      {settingsOpen && <InvoicingSettings onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function ReminderCard({ m, onChanged }) {
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function approveSend() {
    setBusy(true); setErr('');
    try {
      // Approve only what needs approval: an approved reminder, or one whose last attempt may
      // have been delivered, is sent (retried) as is.
      const needsApproval = m.status === 'draft' || (m.status === 'failed' && !m.send_ambiguous);
      if (needsApproval) await api.post(`/invoices/messages/${m.id}/approve`, { revision: m.updated_at });
      await api.post(`/invoices/messages/${m.id}/send`, {});
      onChanged();
    } catch (e) { setErr(e.message); onChanged(); } finally { setBusy(false); }
  }
  async function discard() { try { await api.post(`/invoices/messages/${m.id}/discard`, {}); onChanged(); } catch (e) { setErr(e.message); } }
  return (
    <div className="rounded-lg border border-[var(--border)] p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 break-words"><b>{m.subject}</b> <span className="break-all text-xs text-[var(--text-secondary)]">to {m.to_emails.join(', ')}</span></div>
        <StatusChip status={m.status} />
      </div>
      <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-sans text-xs text-[var(--text-secondary)]">{m.body_text}</pre>
      {m.send_error && <p className="text-xs text-red-600">{m.send_error}</p>}
      <ErrorBox msg={err} />
      <div className="mt-2 flex flex-wrap gap-2">
        <button className={btnCls} disabled={busy} onClick={approveSend}><Send size={13} className="mr-1 inline" />{m.status === 'approved' || m.send_ambiguous ? 'Send' : 'Approve & send'}</button>
        <button className={btn2Cls} disabled={busy} onClick={discard}>Discard</button>
        {m.invoice && <Link className={btn2Cls} to={`/invoices/${m.invoice_id}`}>Open {m.invoice.number}</Link>}
      </div>
    </div>
  );
}

function InvoicingSettings({ onClose }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get('/invoices/settings').then((r) => setS({ ...r.invoicing, remittance_details: r.invoicing.remittance_details || '' })).catch((e) => setMsg(e.message)); }, []);
  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.patch('/invoices/settings', { ...s, remittance_details: s.remittance_details.trim() ? s.remittance_details : null });
      setS({ ...r.invoicing, remittance_details: r.invoicing.remittance_details || '' }); setMsg('Saved. Applies to invoices approved from now on.');
    } catch (e2) { setMsg(e2.message); } finally { setSaving(false); }
  }
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  return (
    <Modal onClose={onClose} title="Invoicing settings">
      {!s ? <ErrorBox msg={msg} /> : (
        <form onSubmit={save}><fieldset disabled={saving} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Labeled t="Number prefix"><input className={inputCls} value={s.number_prefix} onChange={set('number_prefix')} /></Labeled>
          <Labeled t="Digits / next number at least"><div className="flex gap-2"><input className={`${inputCls} w-20`} value={s.number_width} onChange={set('number_width')} /><input className={inputCls} value={s.number_floor} onChange={set('number_floor')} /></div></Labeled>
          <Labeled t="Default payment terms (days)"><input className={inputCls} value={s.default_terms_days} onChange={set('default_terms_days')} /></Labeled>
          <Labeled t="Late charge (% per month)"><input className={inputCls} value={s.late_fee_pct_per_month} onChange={set('late_fee_pct_per_month')} /></Labeled>
          <Labeled t="Remittance details: ACH/wire and check instructions (printed on invoices; blank = “Remittance details on request”)" wide><textarea className={inputCls} rows={4} value={s.remittance_details} onChange={set('remittance_details')} /></Labeled>
          <div className="sm:col-span-2"><button className={btnCls} type="submit">Save</button> <span className="text-xs">{msg}</span></div>
        </fieldset></form>
      )}
    </Modal>
  );
}

const METHODS = ['ach', 'wire', 'check', 'card', 'cash', 'other'];

export function InvoiceDetailPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editErr, setEditErr] = useState('');
  const [pay, setPay] = useState(() => ({ paid_on: easternDay(new Date()), amount: '', method: 'ach', reference: '' }));
  const seq = useRef(0);
  const routeId = useRef(id);
  routeId.current = id;
  const load = useCallback(async () => {
    if (id !== routeId.current) return; // an action's reload for an invoice we've left does nothing
    const mine = ++seq.current;
    try { const r = await api.get(`/invoices/${id}`); if (mine === seq.current && r.invoice.id === routeId.current) { setD(r); setError(''); } } catch (e) { if (mine === seq.current) setError(e.message); }
  }, [id]);
  // Another invoice: clear the old one first so none of its actions stay live.
  useEffect(() => { setD(null); setEdit(null); setConfirmSend(false); load(); }, [load]);
  if (!d || d.invoice.id !== id) return <div className="mx-auto max-w-5xl"><ErrorBox msg={error} />{!error && 'Loading…'}</div>;
  const inv = d.invoice;
  const me = { email: myEmail(), admin: amAdmin() };
  async function act(path, body, ok) {
    setBusy(true); setError(''); setNotice('');
    try { const r = await api.post(`/invoices/${id}${path}`, body || {}); await load(); if (ok) setNotice(ok); return r; } catch (e) { await load(); setError(e.message); return null; } finally { setBusy(false); }
  }
  async function saveEdit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/invoices/${id}`, { revision: inv.updated_at, billing_email: edit.billing_email || null, terms_days: edit.terms_days, notes: edit.notes || null, customer_po: edit.customer_po || null, title: edit.title });
      setEdit(null); setEditErr(''); await load(); setNotice('Saved (back to draft).');
    } catch (e2) { setEditErr(e2.message); } finally { setBusy(false); } // errors shown inside the modal; entered values are kept
  }
  const editable = ['draft', 'approved'].includes(inv.status) && !inv.send_ambiguous;
  return (
    <div className="mx-auto max-w-5xl pb-16">
      <Link to="/invoices" className="mb-2 inline-flex items-center gap-1 text-sm text-[var(--text-secondary)]"><ArrowLeft size={14} /> Invoices</Link>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Invoice {inv.number}</h1>
          <p className="text-sm text-[var(--text-secondary)]">{inv.customer_name} · {inv.title}{d.order ? <> · order <Link className="text-primary" to={`/orders/${d.order.id}`}>{d.order.number}</Link></> : null}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm"><StatusChip status={inv.display_status} />{inv.trigger_note && <span className="text-xs text-[var(--text-secondary)]">{inv.trigger_note}</span>}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={btn2Cls} onClick={() => openAuthed(`/invoices/${id}/pdf`).catch((e) => setError(e.message))}><FileText size={13} className="mr-1 inline" />PDF</button>
          {editable && <button className={btn2Cls} onClick={() => setEdit({ title: inv.title, billing_email: inv.billing_email || '', terms_days: inv.terms_days, notes: inv.notes || '', customer_po: inv.customer_po || '' })}>Edit</button>}
          {inv.status === 'draft' && <button className={btnCls} disabled={busy} onClick={() => act('/approve', { revision: inv.updated_at }, 'Approved. Check the recipient, then send.')}><CheckCircle2 size={14} className="mr-1 inline" />Approve</button>}
          {inv.status === 'approved' && <button className={btnCls} disabled={busy} onClick={() => setConfirmSend(true)}><Send size={14} className="mr-1 inline" />{inv.send_ambiguous ? 'Retry send' : 'Send…'}</button>}
          {d.public_url && <button className={btn2Cls} onClick={() => navigator.clipboard?.writeText(d.public_url).then(() => setNotice('Customer link copied.'))}><Copy size={13} className="mr-1 inline" />Customer link</button>}
          {inv.status === 'sent' && inv.late_fee_accrued > 0 && <button className={btn2Cls} disabled={busy} onClick={async () => { const r = await act('/late-fee', {}, null); if (r) window.location.assign(`/invoices/${r.invoice.id}`); }}><AlertTriangle size={13} className="mr-1 inline" />Draft late charge ({usd(inv.late_fee_accrued)})</button>}
          {inv.status === 'sent' && <button className={btn2Cls} disabled={busy} onClick={() => { // eslint-disable-next-line no-alert
            const note = inv.disputed ? null : window.prompt('What is disputed?'); if (inv.disputed || note !== null) act('/dispute', { disputed: !inv.disputed, note: note || undefined }, inv.disputed ? 'Dispute cleared.' : 'Marked disputed (no reminders while disputed).'); }}>{inv.disputed ? 'Clear dispute' : 'Mark disputed'}</button>}
          {inv.status !== 'void' && !(Number(inv.amount_paid) > 0) && <button className={btn2Cls} disabled={busy} onClick={() => { // eslint-disable-next-line no-alert
            if (window.confirm(`Void ${inv.number}?`)) act('/void', {}, 'Voided.'); }}>Void</button>}
        </div>
      </div>
      <ErrorBox msg={error} />
      {notice && <p className="mb-3 rounded bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}
      {inv.send_error && <p className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">Last send failed: {inv.send_error}</p>}
      {inv.disputed && <p className="mb-3 rounded bg-purple-50 px-3 py-2 text-sm text-purple-800">Disputed: {inv.dispute_note || 'no note'}</p>}

      <section className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-[var(--border)] bg-white p-4 sm:grid-cols-4 dark:bg-slate-900">
        <Field label="Total">{usd(inv.total)}</Field>
        <Field label="Paid">{usd(inv.amount_paid)}</Field>
        <Field label="Balance"><b>{usd(inv.balance)}</b></Field>
        <Field label="Due">{inv.due_date || `${inv.terms_days} days after issue`}{inv.days_overdue > 0 ? ` (${inv.days_overdue}d late)` : ''}</Field>
        <Field label="Bill to">{inv.billing_email || <span className="text-red-600">missing</span>}</Field>
        <Field label="Customer PO" value={inv.customer_po} />
        <Field label="Issued" value={inv.issue_date || 'not yet'} />
        <Field label="Owner (reply-to)" value={inv.owner_email} />
      </section>

      <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
        <h2 className="mb-2 text-sm font-semibold">Lines</h2>
        {(inv.lines || []).map((l) => <p key={l.id} className="flex justify-between gap-3 border-t border-[var(--border)] py-1.5 text-sm first:border-0"><span>{l.description}</span><b>{usd(l.amount)}</b></p>)}
      </section>

      {inv.status === 'sent' && (
        <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
          <h2 className="mb-2 text-sm font-semibold">Payments</h2>
          {d.payments.map((p) => (
            <p key={p.id} className="flex items-center justify-between gap-2 border-t border-[var(--border)] py-1.5 text-sm first:border-0">
              <span>{p.paid_on} · {p.method.toUpperCase()}{p.reference ? ` · ${p.reference}` : ''} <span className="text-xs text-[var(--text-secondary)]">by {p.created_by}</span></span>
              <span className="flex items-center gap-2"><b>{usd(p.amount)}</b>{canRemovePayment(p, inv, me) && <button aria-label="Remove payment" onClick={async () => { try { await api.delete(`/invoices/${id}/payments/${p.id}`); load(); } catch (e) { setError(e.message); } }}><Trash2 size={13} /></button>}</span>
            </p>
          ))}
          {inv.balance > 0 && (
            <form onSubmit={async (e) => { e.preventDefault(); const r = await act('/payments', pay, 'Payment recorded.'); if (r) setPay({ ...pay, amount: '', reference: '' }); }}><fieldset disabled={busy} className="mt-3 flex flex-wrap items-end gap-2">
              <Labeled t="Date"><input type="date" className={inputCls} value={pay.paid_on} onChange={(e) => setPay({ ...pay, paid_on: e.target.value })} /></Labeled>
              <Labeled t="Amount"><input className={`${inputCls} w-32`} inputMode="decimal" placeholder={String(inv.balance)} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} required /></Labeled>
              <Labeled t="Method"><select className={inputCls} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>{METHODS.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}</select></Labeled>
              <Labeled t="Reference"><input className={inputCls} value={pay.reference} placeholder="wire ref / check #" onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></Labeled>
              <button className={btnCls} disabled={busy} type="submit">Record payment</button>
            </fieldset></form>
          )}
        </section>
      )}

      {d.messages.length > 0 && (
        <section className="rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
          <h2 className="mb-2 text-sm font-semibold">Reminders</h2>
          {d.messages.map((m) => <p key={m.id} className="text-sm">{m.stage.replace('_', ' ')} · <StatusChip status={m.status} /> {m.sent_at ? `sent ${m.sent_at.slice(0, 10)}` : ''}</p>)}
        </section>
      )}

      {confirmSend && (
        <Modal onClose={() => setConfirmSend(false)} title={`Send invoice ${inv.number}`}>
          <p className="text-sm">To <b>{inv.billing_email}</b>{inv.cc_emails?.length ? `, cc ${inv.cc_emails.join(', ')}` : ''}. {usd(inv.total)}, due {inv.terms_days} days after today. PDF attached.</p>
          <div className="mt-4 flex gap-2">
            <button className={btnCls} disabled={busy} onClick={async () => { setConfirmSend(false); await act('/send', {}, 'Sent.'); }}>Send now</button>
            <button className={btn2Cls} onClick={() => setConfirmSend(false)}>Cancel</button>
          </div>
        </Modal>
      )}
      {edit && (
        <Modal onClose={() => { setEdit(null); setEditErr(''); }} title={`Edit ${inv.number}`}>
          <form onSubmit={saveEdit}><fieldset disabled={busy} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Labeled t="Title"><input className={inputCls} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Labeled>
            <Labeled t="Billing email"><input className={inputCls} type="email" value={edit.billing_email} onChange={(e) => setEdit({ ...edit, billing_email: e.target.value })} /></Labeled>
            <Labeled t="Payment terms (days)"><input className={inputCls} value={edit.terms_days} onChange={(e) => setEdit({ ...edit, terms_days: e.target.value })} /></Labeled>
            <Labeled t="Customer PO"><input className={inputCls} value={edit.customer_po} onChange={(e) => setEdit({ ...edit, customer_po: e.target.value })} /></Labeled>
            <Labeled t="Notes (printed)" wide><textarea className={inputCls} rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></Labeled>
            <div className="sm:col-span-2"><ErrorBox msg={editErr} /><button className={btnCls} type="submit">Save</button></div>
          </fieldset></form>
        </Modal>
      )}
    </div>
  );
}

/** Billing block for the order page: milestone schedule, each milestone's invoice, create/sync. */
export function OrderBilling({ orderId }) {
  const [b, setB] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setB(await api.get(`/invoices/orders/${orderId}/billing`)); setErr(''); } catch (e) { setErr(e.status === 403 ? '' : e.message); } }, [orderId]);
  useEffect(() => { load(); }, [load]);
  if (!b) return err ? <ErrorBox msg={err} /> : null;
  return (
    <section className="mb-4 rounded-xl border border-[var(--border)] bg-white p-4 dark:bg-slate-900">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold"><Receipt size={15} /> Billing</h2>
        <span className="text-xs text-[var(--text-secondary)]">Invoiced {usd(b.invoiced)} · paid {usd(b.paid)} of {usd(b.order.total)}</span>
      </div>
      <ErrorBox msg={err} />
      {b.schedule.map((m) => (
        <div key={m.key} className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border)] py-2 text-sm first:border-0">
          <div className="min-w-0"><p>{m.label}</p><p className="text-xs text-[var(--text-secondary)]">{m.num}/{m.den} · {usd(m.amount)} · trigger: {m.trigger.replace('_', ' ')} {m.reached ? '(reached)' : '(not yet)'}</p></div>
          {m.invoice
            ? <Link className="flex items-center gap-2" to={`/invoices/${m.invoice.id}`}><span className="font-mono text-xs text-primary">{m.invoice.number}</span><StatusChip status={m.invoice.display_status} /></Link>
            : <button className={btn2Cls} onClick={async () => { try { await api.post(`/invoices/orders/${orderId}/milestones/${m.key}`, {}); load(); } catch (e) { setErr(e.message); } }}>Create invoice now</button>}
        </div>
      ))}
      <button className={`${btn2Cls} mt-2`} onClick={async () => { try { await api.post(`/invoices/orders/${orderId}/sync`, {}); load(); } catch (e) { setErr(e.message); } }}>Check milestones</button>
    </section>
  );
}
